import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Divider, Flex,
  Group,
  MultiSelect,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Timeline,
  Tooltip
} from "@mantine/core";
import {
  getObjectId,
  ServiceEventEntity,
  ServiceEventLocationFacet,
  ServiceEventLocationFacetHistory,
  serviceEventLocationFacetsSelector,
  ServiceEventLocationServiceType, SiteEntity,
  useGetServiceEventLocationFacetsQuery
} from "@ots-frontend/ots-data";
import React, {useMemo, useRef, useState} from "react";
import {IconEdit, IconRefresh, IconSearch} from "@tabler/icons-react";
import {FormattedDateTime, getFormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {useVirtualizer} from "@tanstack/react-virtual";
import {getRefServiceStatusColorRef, getRefServiceStatusDisplayValue} from "../../../util/ref-service-status-ui-util";
import {getRefServiceStatusFacetDisplayValue} from "../../../util/ref-service-status-facet-ui-util";
import {getRefServiceModeDisplayValue} from "../../../util/ref-service-mode-ui-util";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";

export type ServiceEventLocationFacetMasterDetailProps = {
  serviceEvent: ServiceEventEntity
}

export const ServiceEventLocationFacetMasterDetail = (props: ServiceEventLocationFacetMasterDetailProps) => {
  const {serviceEvent} = props

  const [search, setSearch] = useState('');
  const [selectedStatusFilters, setSelectedStatusFilters] = useState<string[]>([]);
  const [selectedFacetFilters, setSelectedFacetFilters] = useState<string[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string | undefined>(undefined);

  const selectServiceEventLocationFacets = useMemo(() => {
    return serviceEventLocationFacetsSelector()
  }, []);

  const {locations, isFetching, refetch} = useGetServiceEventLocationFacetsQuery(serviceEvent.id, {
    selectFromResult: (result) => ({
      ...result,
      locations: selectServiceEventLocationFacets(result),
      isFetching: result.isFetching,
    })
  });

  const allStatuses = useMemo(
    () =>
      Array.from(
        new Set(
          locations.flatMap((s) =>
            s.serviceTypes.flatMap((st) => st.facetHistory.map((h) => h.status))
          )
        )
      ),
    [locations]
  );

  const allFacets = useMemo(
    () =>
      Array.from(
        new Set(
          locations.flatMap((s) =>
            s.serviceTypes.flatMap((st) => st.facetHistory.map((h) => h.facet))
          )
        )
      ),
    [locations]
  );

  // filter sites for left list based on search & global facet/status filters
  const filteredSites: ServiceEventLocationFacet[] = useMemo(() => {
    return locations.filter((s) => {
      if (search && !s.siteName.toLowerCase().includes(search.toLowerCase())) {
        return false;
      }

      // if no filters, keep
      if (selectedStatusFilters.length === 0 && selectedFacetFilters.length === 0) {
        return true;
      }

      // keep site if ANY facet event at this site matches filters
      return s.serviceTypes.some((st) =>
        st.facetHistory.some((h) => {
          const statusOk =
            selectedStatusFilters.length === 0 ||
            selectedStatusFilters.includes(h.status);
          const facetOk =
            selectedFacetFilters.length === 0 ||
            selectedFacetFilters.includes(h.facet);
          return statusOk && facetOk;
        })
      );
    });
  }, [locations, search, selectedStatusFilters, selectedFacetFilters]);

  // ensure selected site remains valid
  const effectiveSelectedSiteId =
    selectedSiteId && filteredSites.some((s) => s.siteId === selectedSiteId)
      ? selectedSiteId
      : filteredSites[0]?.siteId ?? null;

  const selectedSite = filteredSites.find((s) => s.siteId === effectiveSelectedSiteId) ?? null;

  return (
    <Stack spacing="xs" h="100%">
      {/* Header */}
      <Group align="flex-end" position="apart">
        <Group spacing="sm" w="30%">
          <ActionIcon onClick={() => refetch()}>
            <IconRefresh size={16} />
          </ActionIcon>
          <TextInput
            placeholder="Search sites..."
            rightSection={<IconSearch size={16}/>}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w="85%"
          />
        </Group>
        <Group spacing="sm">
          <MultiSelect
            label="Status filter"
            placeholder="Any status"
            value={selectedStatusFilters}
            onChange={setSelectedStatusFilters}
            data={allStatuses.map((s) => ({value: s, label: s}))}
            clearable
            searchable
            nothingFound="No statuses"
            w={220}
          />
          <MultiSelect
            label="Facet filter"
            placeholder="Any facet"
            value={selectedFacetFilters}
            onChange={setSelectedFacetFilters}
            data={allFacets.map((f) => ({value: f, label: f}))}
            clearable
            searchable
            nothingFound="No facets"
            w={220}
          />
        </Group>
      </Group>

      <Divider variant="dotted"/>

      {/* Main layout: left site list, right detail */}
      <Group position="apart" align="flex-start" spacing="md" noWrap>
        {/* Left: sites list */}
        <LocationList
          locations={filteredSites}
          selectedLocationId={effectiveSelectedSiteId}
          onSelect={setSelectedSiteId}
          height={500}
        />

        {/* Right: detail / timelines */}
        <Card withBorder radius="md" padding="md" w="70%" h="100%">
          {selectedSite ? (
            <LocationDetailPanel
              location={selectedSite}
            />
          ) : (
            <Center mih={120}>
              <Text size="sm" color="dimmed">
                Select a site to view its facet timeline.
              </Text>
            </Center>
          )}
        </Card>
      </Group>
    </Stack>
  );
};

interface LocationListProps {
  locations: ServiceEventLocationFacet[];
  selectedLocationId: string | null;
  onSelect: (id: string) => void;
  height?: number; // scroll height
}

export const LocationList = (props: LocationListProps) => {
  const {
    locations,
    selectedLocationId,
    onSelect,
    height = 600
  } = props;

  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: locations.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 90, // average list item height
    overscan: 10,
  });

  const items = virtualizer.getVirtualItems()

  return (
    <Card withBorder radius="md" padding="xs" w="30%" mih={height} mb="xs">
      <Text size="sm" fw={500} mb="xs">
        Locations ({locations.length})
      </Text>

      <div
        ref={parentRef}
        style={{
          height,
          overflow: "auto",
          position: "relative",
        }}
      >
        <div
          style={{
            height: virtualizer.getTotalSize(),
            width: "100%",
            position: "relative",
          }}
        >
          {items.map((virtualRow) => {
            const location = locations[virtualRow.index];
            return (
              <div
                key={virtualRow.key}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  transform: `translateY(${virtualRow.start}px)`,
                  padding: 4,
                }}
              >
                <LocationListItem
                  location={location}
                  selected={location.siteId === selectedLocationId}
                  onSelect={() => onSelect(location.siteId)}
                />
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
};

interface LocationListItemProps {
  location: ServiceEventLocationFacet;
  selected: boolean;
  onSelect: () => void;
}

const LocationListItem = (props: LocationListItemProps) => {
  const {location, selected, onSelect} = props;

  const { t } = useTranslation(["common"])

  const allHistory = location.serviceTypes.flatMap((st) => st.facetHistory);

  // Latest per facet for summary chips
  const latestByFacet: Record<string, ServiceEventLocationFacetHistory | undefined> = useMemo(() => {
    const grouped = groupByFacet(allHistory);
    const result: Record<string, ServiceEventLocationFacetHistory | undefined> = {};
    for (const facet of Object.keys(grouped)) {
      const sorted = sortHistory(grouped[facet]);
      result[facet] = sorted[sorted.length - 1];
    }
    return result;
  }, [allHistory]);

  return (
    <Card
      withBorder
      radius="md"
      p="xs"
      onClick={onSelect}
      style={{
        cursor: "pointer",
        borderColor: selected ? "var(--mantine-color-blue-5)" : undefined,
        backgroundColor: selected ? "var(--mantine-color-blue-0)" : undefined,
        boxSizing: "border-box",
      }}
    >
      <Stack spacing={2}>
        <Group position="apart" align="flex-start" spacing="xs" noWrap>
          {/* Title (multi-line allowed) */}
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text
              size="sm"
              weight={500}
              lineClamp={3}       // 👈 allow up to 3 lines
              style={{ wordBreak: "break-word" }}
            >
              {location.siteName}
            </Text>
          </Box>

          {/* Badge (pinned right) */}
          <Badge size="xs" variant="light">
            {location.serviceTypes.length} svc types
          </Badge>
        </Group>

        <Text size="xs" c="dimmed">
          Joined: {getFormattedDateTime(location.joinedDttm)} · Left:{' '}
          {location.leftDttm ? getFormattedDateTime(location.leftDttm) : 'still in event'}
        </Text>

        <Group spacing={4} mt={4}>
          {Object.entries(latestByFacet).map(([facet, latest]) =>
            latest ? (
              <Tooltip
                key={facet}
                label={`${getRefServiceStatusFacetDisplayValue(facet, t)}: ${getRefServiceStatusDisplayValue(latest.status, t)} (${getRefServiceModeDisplayValue(latest.serviceMode, t)}) @ ${getFormattedDateTime(
                  latest.asOfDttm
                )}`}
                withinPortal={true}
              >
                <Badge size="xs" c={getRefServiceStatusColorRef(latest.status)} variant="light">
                  {getRefServiceStatusFacetDisplayValue(facet, t)}: {getRefServiceStatusDisplayValue(latest.status, t)}
                </Badge>
              </Tooltip>
            ) : null
          )}
        </Group>
      </Stack>
    </Card>
  );
};

interface LocationDetailPanelProps {
  location: ServiceEventLocationFacet;
}

const LocationDetailPanel = (props: LocationDetailPanelProps) => {
  const {location} = props;

  const serviceTypes = location.serviceTypes;

  return (
    <Stack spacing="xs" h="100%">
      <Group position="apart">
        <Box>
          <Text fw={500}>{location.siteName}</Text>
          <Text size="xs" c="dimmed">
            Joined: {getFormattedDateTime(location.joinedDttm)} · Left:{' '}
            {location.leftDttm ? getFormattedDateTime(location.leftDttm) : 'still in event'}
          </Text>
        </Box>
        <Badge variant="light">
          Service types: {serviceTypes.length}/{location.serviceTypes.length}
        </Badge>
      </Group>
      <Divider/>

      <ScrollArea h="100%">
        <Stack spacing="md">
          {serviceTypes.map((st) => (
            <ServiceTypeTimelineCard key={st.serviceTypeId} serviceType={st} site={location.siteId}/>
          ))}
          {serviceTypes.length === 0 && (
            <Text size="sm" c="dimmed">
              No facet events for this site match current filters.
            </Text>
          )}
        </Stack>
      </ScrollArea>
    </Stack>
  );
};

interface ServiceTypeTimelineCardProps {
  site: SiteEntity | string
  serviceType: ServiceEventLocationServiceType;
}

const ServiceTypeTimelineCard = (props: ServiceTypeTimelineCardProps) => {
  const {site, serviceType} = props;

  const { t } = useTranslation(["common"])
  const navigate = useNavigate();

  const historyByFacet = groupByFacet(serviceType.facetHistory);

  return (
    <Card withBorder radius="md" padding="sm">
      <Flex align="center" justify="space-between" mb="xs">
        <Text size="sm" fw={500} mb={4}>
          {serviceType.serviceTypeName}
        </Text>
        <Button
          variant="subtle"
          size="xs"
          leftIcon={<IconEdit size={14}/>}
          onClick={() => navigate("/location-statuses/form", {
            state: {
              formDefaults: {
                site: getObjectId(site),
              }
            }
          })}>
          {t("common.map.infopanel.siteops.action.setstatus", "Set Status")}
        </Button>
      </Flex>
      <Stack spacing="xs">
        {Object.entries(historyByFacet).map(([facet, entries]) => (
          <FacetTimeline key={facet} facet={facet} entries={sortHistory(entries)}/>
        ))}
      </Stack>
    </Card>
  );
};

interface FacetTimelineProps {
  facet: string;
  entries: ServiceEventLocationFacetHistory[];
}

const FacetTimeline = (props: FacetTimelineProps) => {
  const {facet, entries} = props;

  const { t } = useTranslation(["common"])

  return (
    <Card withBorder radius="md" padding="xs">
      <Text size="xs" fw={500} mb={6}>
        Facet: {getRefServiceStatusFacetDisplayValue(facet, t)}
      </Text>
      <Timeline bulletSize={14} lineWidth={2}>
        {entries.map((e) => (
          <Timeline.Item
            key={e.locationStatusFacetId + e.asOfDttm}
            color={getRefServiceStatusColorRef(e.status)}
            title={`${getRefServiceStatusDisplayValue(e.status, t)} (${getRefServiceModeDisplayValue(e.serviceMode, t)})`}
          >
            <Text size="xs" c="dimmed">
              <FormattedDateTime date={e.asOfDttm}/>
            </Text>
            <Text size="xs">
              Source: <Text span fw={500}>{e.source}</Text> · Confidence:{' '}
              {e.confidencePct?.toFixed(1)}%
            </Text>
            {e.note && (
              <Text size="xs" c="dimmed">
                {e.note}
              </Text>
            )}
          </Timeline.Item>
        ))}
      </Timeline>
    </Card>
  );
};

function groupByFacet(
  history: ServiceEventLocationFacetHistory[]
): Record<string, ServiceEventLocationFacetHistory[]> {
  return history.reduce<Record<string, ServiceEventLocationFacetHistory[]>>((acc, h) => {
    if (!acc[h.facet]) acc[h.facet] = [];
    acc[h.facet].push(h);
    return acc;
  }, {});
}

function sortHistory(history: ServiceEventLocationFacetHistory[]): ServiceEventLocationFacetHistory[] {
  return [...history].sort(
    (a, b) => new Date(a.asOfDttm).getTime() - new Date(b.asOfDttm).getTime()
  );
}
